import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ulid } from '../lib/ulid';
import type { Exercise } from '@nelnela/contracts';
import { api, ApiError } from '../lib/api';
import {
  axisOf,
  backText,
  canDefer,
  CARD_KINDS,
  current,
  defer,
  flip,
  frontText,
  revealText,
  initSession,
  isAudioFront,
  isDone,
  isGradable,
  resolve,
  summarise,
  type Outcome,
  type Session,
} from '../lib/cards';
import { ttsPlayer } from '../lib/tts-player';
import { startActivity, type ActivityHandle } from '../lib/session';
import { useStudy } from '../app/study';

/**
 * The card session (M10-iv), to M10-α's frames.
 *
 * THE SCREEN IS WIRING. Every decision it makes lives in `lib/cards.ts` and is tested there,
 * because the web suite is node with no DOM — the split `lib/journal.ts` and `lib/chat.ts` already
 * make. What is here is layout, the two network calls, and the α decisions that are *about*
 * rendering and could not be anywhere else.
 *
 * The α calls this file is obeying, all binding:
 *   · the flip is a TAP ON THE WHOLE CARD, 250 ms turn. Swipe was rejected — no hidden gestures
 *     in a grading UI.
 *   · CARD FRONTS NEVER MOUNT `.nn-reading`; BACKS ALWAYS DO. The front is a single word or a
 *     prompt and wants display leading; the back carries a sentence and wants reading leading.
 *   · the judgment pair rides `--nn-primary-*`, NOT ember. Ember is the learner-acting accent and
 *     "not yet / knew it" is a judgment, not an action.
 *   · the card's radius is the literal 16, deliberately not minted as a token.
 *   · the summary's facts panels stay FLAT `--nn-panel` — the subject is the numbers, so no hero
 *     gradient. "Done" is the one ember fill on the screen.
 */

const CARD_RADIUS = 16; // α: a literal, deliberately not minted
const FLIP_MS = 250; // α: the turn

export function Cards() {
  const study = useStudy();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activityRef = useRef<ActivityHandle | null>(null);

  useEffect(() => {
    let cancelled = false;
    let handle: ActivityHandle | null = null;
    void (async () => {
      try {
        const a = await startActivity(study.session, 'review', undefined, {});
        if (cancelled) {
          void a.end();
          return;
        }
        handle = a;
        activityRef.current = a;
        const list = await api.exerciseNext({
          activityId: a.id,
          kinds: [...CARD_KINDS],
          count: 12,
          // The saved leg. Without it a learner whose queue is empty gets nothing, which is the
          // bug this screen's first real run turned up.
          includeSaved: true,
        });
        if (!cancelled) setSession(initSession(list, Date.now()));
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.problem.status === 403
            ? 'Cards are part of the paid layer.'
            : 'Couldn’t deal the cards. They’ll be here when the connection is.',
        );
      }
    })();
    return () => {
      cancelled = true;
      void handle?.end();
    };
  }, [study.session]);

  /** Judge a flip card: the answer is the judgment, and the card advances immediately. */
  const judge = useCallback(
    async (ex: Exercise, outcome: Outcome, selfGrade?: 'good' | 'again') => {
      const activityId = activityRef.current?.id;
      setSession((s) => (s ? resolve(s, outcome) : s));
      if (!activityId) return;
      try {
        await api.exerciseAnswer({
          exerciseId: ex.id,
          activityId,
          answer: '',
          ...(selfGrade ? { selfGrade } : { answerMode: 'shown' as const }),
          latencyMs: 0,
          hintsUsed: 0,
          eventId: ulid(),
        });
      } catch {
        // The card is already resolved on screen. A failed write is not worth interrupting a
        // session for, and the event is idempotent by its ULID if the client ever retries.
      }
    },
    [],
  );

  /**
   * Submit a typed answer and hand back the SERVER's verdict.
   *
   * Separate from `judge` because the order is the other way round: a production card is graded
   * first and advances only after the learner has read the verdict. The card is not resolved here.
   */
  const submitTyped = useCallback(async (ex: Exercise, typed: string) => {
    const activityId = activityRef.current?.id;
    if (!activityId) return { correct: false, expected: '' };
    const res = await api.exerciseAnswer({
      exerciseId: ex.id,
      activityId,
      answer: typed,
      answerMode: 'typed',
      latencyMs: 0,
      hintsUsed: 0,
      eventId: ulid(),
    });
    return { correct: res.correct, expected: String(res.expected ?? '') };
  }, []);

  if (error) return <Centered>{error}</Centered>;
  if (!session) return <Centered>Dealing…</Centered>;
  if (session.cards.length === 0)
    return (
      <Centered>
        Nothing due, and nothing saved to practise. Read something and save a line — it’ll be here
        tomorrow.
      </Centered>
    );
  if (isDone(session))
    return <SessionSummary session={session} onDone={() => navigate('/practice')} />;

  const card = current(session)!;
  return (
    <Card
      key={card.exercise.id}
      card={card}
      n={session.idx + 1}
      of={session.cards.length}
      deferrable={canDefer(session)}
      onDefer={() => setSession((s) => (s ? defer(s) : s))}
      onLeave={() => navigate('/practice')}
      onFlip={() => setSession((s) => (s ? flip(s) : s))}
      onJudge={(verdict) => {
        const ex = card.exercise;
        // A card with no item writes no event, so it is not a judgment — it is practice. The
        // summary counts it in its own column, and the request is still sent: the server is the
        // one that decides nothing lands, and a client that skipped the call would be guessing.
        const outcome: Outcome = isGradable(ex)
          ? { kind: 'judged', verdict }
          : { kind: 'practised' };
        void judge(ex, outcome, verdict);
      }}
      onSubmit={(typed) => submitTyped(card.exercise, typed)}
      onContinue={(typed, correct, expected) =>
        setSession((s) => (s ? resolve(s, { kind: 'typed', correct, expected, typed }) : s))
      }
    />
  );
}

// --- one card ----------------------------------------------------------------------------------

function Card({
  card,
  n,
  of,
  deferrable,
  onDefer,
  onLeave,
  onFlip,
  onJudge,
  onSubmit,
  onContinue,
}: {
  card: { exercise: Exercise; flipped: boolean };
  n: number;
  of: number;
  deferrable: boolean;
  onDefer: () => void;
  onLeave: () => void;
  onFlip: () => void;
  onJudge: (verdict: 'good' | 'again') => void;
  onSubmit: (typed: string) => Promise<{ correct: boolean; expected: string }>;
  onContinue: (typed: string, correct: boolean, expected: string) => void;
}) {
  const ex = card.exercise;
  const production = ex.kind === 'flash_production';
  return (
    // Full height and a flex column, so the judgment pair can live at the BOTTOM: a session is
    // ~24 one-handed taps, and the first build put every one of them in the top-of-screen stretch
    // zone (2026-09-03 walkthrough). Mantine's AppShell.Main is min-height only, so '100%' would
    // resolve to nothing — the shell's own footer var is the honest offset.
    <div
      style={{
        background: 'var(--nn-wash)',
        minHeight: 'calc(100dvh - var(--app-shell-footer-height, 0px))',
        display: 'flex',
        flexDirection: 'column',
        padding: '18px 16px 24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: 11,
          letterSpacing: '.08em',
          color: 'var(--nn-faint)',
          textTransform: 'uppercase',
          marginBottom: 14,
        }}
      >
        <span>
          {n} of {of}
        </span>
        {/* The wizard's quit carries "nothing saved"; a card session's exit carries the opposite
            contract — every answered card is already recorded (the route writes per answer), and
            the rest simply stay due. Leaving was always harmless; now it is also visible. */}
        <button
          type="button"
          onClick={onLeave}
          style={{
            all: 'unset',
            cursor: 'pointer',
            fontSize: 11,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--nn-muted)',
            padding: '4px 0 4px 12px',
          }}
        >
          done for now
        </button>
      </div>

      {production ? (
        <ProductionCard ex={ex} onSubmit={onSubmit} onContinue={onContinue} />
      ) : (
        <MeaningCard
          ex={ex}
          flipped={card.flipped}
          deferrable={deferrable}
          onDefer={onDefer}
          onFlip={onFlip}
          onJudge={onJudge}
        />
      )}
    </div>
  );
}

const heroCard = {
  background: 'var(--nn-hero)',
  borderRadius: CARD_RADIUS,
  borderTop: '1px solid var(--nn-hairline-lit)',
  boxShadow: '0 18px 44px rgba(0,0,0,.5), 0 2px 6px rgba(0,0,0,.3)',
  padding: '28px 20px',
} as const;

function MeaningCard({
  ex,
  flipped,
  deferrable,
  onDefer,
  onFlip,
  onJudge,
}: {
  ex: Exercise;
  flipped: boolean;
  deferrable: boolean;
  onDefer: () => void;
  onFlip: () => void;
  onJudge: (verdict: 'good' | 'again') => void;
}) {
  const audio = isAudioFront(ex);
  const front = frontText(ex);
  const back = backText(ex);
  const reveal = revealText(ex);
  return (
    // The face centres in the space the header leaves; the judgment pair sits at the bottom,
    // in thumb reach. When the card is face-down there is no pair and the face owns the room.
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {/* α: the WHOLE card is the flip target. Not a button inside it, and not a swipe. */}
        <div
          role="button"
          tabIndex={0}
          aria-label={flipped ? 'card, face up' : 'card, tap to flip'}
          onClick={flipped ? undefined : onFlip}
          onKeyDown={(e) => {
            if (!flipped && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onFlip();
            }
          }}
          style={{
            ...heroCard,
            cursor: flipped ? 'default' : 'pointer',
            transition: `transform ${FLIP_MS}ms ease`,
            minHeight: 210,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            textAlign: 'center',
          }}
        >
          {audio && (
            <button
              type="button"
              aria-label="play"
              onClick={(e) => {
                // stopPropagation, because the whole card is the flip target and pressing play is
                // not a request to turn it over — the learner is listening again, which is the
                // thing an audio card exists to let them do.
                e.stopPropagation();
                if (ex.prompt.audio)
                  void ttsPlayer
                    .play({
                      url: ex.prompt.audio.url,
                      title: 'Card',
                      subtitle: 'cards',
                      key: `cards:${ex.id}`,
                    })
                    .catch(() => undefined);
              }}
              style={{
                width: 48,
                height: 48,
                borderRadius: 24,
                border: 'none',
                background: 'var(--nn-info)',
                color: 'var(--nn-info-text)',
                fontSize: 18,
                cursor: 'pointer',
              }}
            >
              ▶
            </button>
          )}
          {/* α: fronts NEVER mount .nn-reading. A single word wants display leading. */}
          {front && !flipped && (
            <div style={{ fontSize: 30, color: 'var(--nn-text-strong)' }}>{front}</div>
          )}

          {flipped && (
            /* α: backs ALWAYS do — the back carries a sentence, and a sentence wants reading. */
            <div className="nn-reading" style={{ width: '100%' }}>
              {reveal && (
                <div style={{ fontSize: 26, color: 'var(--nn-text-strong)', marginBottom: 6 }}>
                  {reveal}
                </div>
              )}
              <div style={{ fontSize: 15, color: 'var(--nn-text)' }}>{back}</div>
              {ex.prompt.context && (
                <div style={{ marginTop: 14, fontSize: 14, color: 'var(--nn-muted)' }}>
                  {ex.prompt.context}
                </div>
              )}
            </div>
          )}

          {!flipped && <div style={{ fontSize: 12, color: 'var(--nn-faint)' }}>tap to flip</div>}
        </div>

        {/* The sound-off escape — audio fronts only, and only while there is somewhere to defer
            TO (lib/cards.ts `canDefer`). A written card needs no escape, and a flipped card has
            already shown its answer. */}
        {audio && !flipped && deferrable && (
          <button
            type="button"
            onClick={onDefer}
            style={{
              all: 'unset',
              cursor: 'pointer',
              alignSelf: 'center',
              marginTop: 14,
              fontSize: 12,
              color: 'var(--nn-muted)',
              padding: 4,
            }}
          >
            can’t listen right now — later ↷
          </button>
        )}
      </div>

      {flipped && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16, paddingBottom: 6 }}>
          <button
            type="button"
            onClick={() => onJudge('again')}
            style={{
              flex: 1,
              padding: '13px 0',
              borderRadius: 12,
              border: '1px solid var(--nn-hairline)',
              background: 'transparent',
              color: 'var(--nn-text)',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            not yet
          </button>
          {/* α: the judgment pair rides --nn-primary-*, NOT ember. */}
          <button
            type="button"
            onClick={() => onJudge('good')}
            style={{
              flex: 1,
              padding: '13px 0',
              borderRadius: 12,
              border: '1px solid var(--nn-primary-border)',
              background: 'var(--nn-primary-fill)',
              color: 'var(--nn-primary-text)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            knew it
          </button>
        </div>
      )}
    </div>
  );
}

function ProductionCard({
  ex,
  onSubmit,
  onContinue,
}: {
  ex: Exercise;
  /** submits through the parent, which owns the activity id, and returns the server's verdict */
  onSubmit: (typed: string) => Promise<{ correct: boolean; expected: string }>;
  onContinue: (typed: string, correct: boolean, expected: string) => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState<{ correct: boolean; expected: string } | null>(null);

  /**
   * THE SERVER DECIDES, and the client never guesses.
   *
   * The leniency that accepts any attested form of the lemma is a database question — it reads
   * `lemma_cache` and `catalog_form`. A client-side string comparison would mark a right answer
   * wrong before the request was ever made, and the learner would see "close — one letter off"
   * about a word they had actually produced.
   */
  const check = async () => {
    if (busy || !typed.trim()) return;
    setBusy(true);
    try {
      const res = await onSubmit(typed);
      setVerdict(res);
    } finally {
      setBusy(false);
    }
  };

  return (
    // Centred like the meaning card; the controls stay inside the card, above where the keyboard
    // lands, so typing never scrolls the face away.
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={heroCard}>
        <div style={{ fontSize: 20, color: 'var(--nn-text-strong)', marginBottom: 16 }}>
          {frontText(ex)}
        </div>
        {verdict ? (
          <Verdict
            verdict={verdict}
            typed={typed}
            onContinue={() => onContinue(typed, verdict.correct, verdict.expected)}
          />
        ) : (
          <>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="in Georgian"
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: 12,
                border: '1px solid var(--nn-hairline)',
                background: 'var(--nn-well)',
                color: 'var(--nn-text-strong)',
                fontSize: 17,
              }}
            />
            <button
              type="button"
              disabled={!typed.trim() || busy}
              onClick={() => void check()}
              style={{
                marginTop: 14,
                width: '100%',
                padding: '13px 0',
                borderRadius: 12,
                border: '1px solid var(--nn-primary-border)',
                background: 'var(--nn-primary-fill)',
                color: 'var(--nn-primary-text)',
                fontSize: 14,
                fontWeight: 600,
                cursor: typed.trim() ? 'pointer' : 'default',
                opacity: typed.trim() ? 1 : 0.5,
              }}
            >
              Continue →
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** α: the coral pair is theme-invariant — identical to v1 in both schemes. */
const CORAL = { color: '#e0938c', background: 'rgba(224,100,92,.14)' } as const;

function Verdict({
  verdict,
  typed,
  onContinue,
}: {
  verdict: { correct: boolean; expected: string };
  typed: string;
  onContinue: () => void;
}) {
  return (
    <div>
      <Fact label="expected" value={verdict.expected} />
      <div style={{ height: 8 }} />
      <Fact label="yours" value={typed} style={verdict.correct ? undefined : CORAL} />
      <button
        type="button"
        onClick={onContinue}
        style={{
          marginTop: 16,
          width: '100%',
          padding: '13px 0',
          borderRadius: 12,
          border: '1px solid var(--nn-primary-border)',
          background: 'var(--nn-primary-fill)',
          color: 'var(--nn-primary-text)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Continue →
      </button>
    </div>
  );
}

function Fact({
  label,
  value,
  style,
}: {
  label: string;
  value: string;
  style?: { color: string; background: string };
}) {
  return (
    <div
      style={{
        background: style?.background ?? 'var(--nn-well)',
        borderRadius: 12,
        padding: '10px 13px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--nn-faint)',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 17, color: style?.color ?? 'var(--nn-text-strong)' }}>{value}</div>
    </div>
  );
}

// --- the summary -------------------------------------------------------------------------------

function SessionSummary({ session, onDone }: { session: Session; onDone: () => void }) {
  const s = summarise(session, Date.now());
  return (
    <div style={{ background: 'var(--nn-wash)', minHeight: '100%', padding: '22px 16px 24px' }}>
      <div style={{ fontSize: 19, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
        Cards — done
      </div>
      <div style={{ fontSize: 13, color: 'var(--nn-muted)', marginTop: 3 }}>
        {s.cards} cards · {s.minutes} {s.minutes === 1 ? 'minute' : 'minutes'}
      </div>

      {/* α: facts panels stay FLAT --nn-panel — the subject is the numbers, so no hero gradient. */}
      <Panel title="seen, per axis">
        <div style={{ display: 'flex', gap: 18 }}>
          {(['read', 'listen', 'produce'] as const).map((axis) => (
            <div key={axis}>
              <div style={{ fontSize: 20, color: 'var(--nn-text-strong)' }}>{s.perAxis[axis]}</div>
              <div style={{ fontSize: 11, color: 'var(--nn-faint)' }}>{axis}</div>
            </div>
          ))}
        </div>
      </Panel>

      {s.moved.length > 0 && (
        <Panel title="what moved">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {s.moved.map((w, i) => (
              <span
                key={`${w}-${i}`}
                style={{
                  fontSize: 14,
                  color: 'var(--nn-text)',
                  border: '1px solid var(--nn-hairline)',
                  borderRadius: 999,
                  padding: '4px 11px',
                }}
              >
                {w}
              </span>
            ))}
          </div>
        </Panel>
      )}

      {/*
        α: the summary OWNS the no-re-deal rule, and says it in the engine's own terms. FSRS
        short-term scheduling is off by design, so a card seen twice today collapses to one
        worst-grade review — re-dealing tonight's misses would teach the model nothing.
        `practised` is its own clause and never folded into either verdict: those cards wrote no
        event, so counting them either way would be a claim about evidence that does not exist.
      */}
      <div style={{ marginTop: 16, fontSize: 13, color: 'var(--nn-muted)', lineHeight: 1.55 }}>
        {s.notYet} not yet · {s.knewIt} knew it
        {s.practised > 0 && ` · ${s.practised} practised`}
        {s.notYet > 0 && (
          <>
            {' '}
            — the misses come back tomorrow. Memory works in days; re-dealing them tonight would
            teach it nothing.
          </>
        )}
        {s.practised > 0 && (
          <>
            {' '}
            Saved phrases with no word attached are practice, not a score — nothing was recorded for
            them.
          </>
        )}
      </div>

      {/* α: "Done" is the one ember fill on this screen. */}
      <button
        type="button"
        onClick={onDone}
        style={{
          marginTop: 20,
          width: '100%',
          padding: '13px 0',
          borderRadius: 12,
          border: 'none',
          background: 'var(--nn-accent)',
          color: 'var(--nn-on-accent)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Done
      </button>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 16,
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 12,
        padding: '13px 14px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--nn-faint)',
          marginBottom: 9,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--nn-wash)',
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        color: 'var(--nn-muted)',
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}

export { axisOf };
